import { useMutation, useQuery } from "react-query";
import { AnalyticTable, TNewConfig } from "../api/analyticTable";
import { useCallback, useMemo, useState } from "react";
import { TAnalyticParams } from "../shared/types/analyticParameters.type";
import { get, isUndefined, toNumber, uniqBy } from "lodash";
import { TuserAccountType } from "../shared/types/user.type";
import { TAnalyticView } from "../shared/types/analyticView.type";
import { TAnalyticData } from "../pages/analytics";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { transform } from "../transformer/ExportAnalyticPayload";
import { hydraTransform } from "../shared/utils/hydraTransformer";
import i18next from "i18next";
import { useLocales } from "./useLocales";

// 1. Static Blacklist Set for O(1) lookups
const PARAM_BLACKLIST = new Set(["training.date", "horse.name"]);

// 2. Static Map for Insight Parameters to prevent re-creating large arrays on every call
const INSIGHT_PARAMETERS_MAP: Partial<Record<TuserAccountType, string[]>> = {
  TROT: [
    "horse.age",
    "horse.sex",
    "training.training_type_name",
    "training.track_name_name",
    "training.track_condition_name",
    "training.rider_name",
    "insight.duration_seconds",
    "insight.distance_meters",
    "insight.hand_turn",
    "insight.trot_avg_speed",
    "insight.max_speed",
    "insight.speed_best_2000",
    "insight.speed_best_1500",
    "insight.speed_best_500",
    "insight.speed_best_250",
    "insight.speed_last_1000",
    "insight.speed_last_500",
    "insight.speed_last_250",
    "insight.max_acceleration",
    "insight.z5_duration",
    "insight.z5_distance",
    "insight.z5_speed",
    "insight.max_heart_rate",
    "insight.time_to_100bpm",
    "insight.bpm_at_end",
    "insight.bpm_at_end_percent",
    "insight.mean_hr_first_trot",
    "insight.mean_speed_first_trot",
    "insight.mean_symmetry_first_trot",
    "insight.mean_regularity_first_trot",
    "insight.stride_frequency_at_45",
    "insight.stride_length_at_45",
    "insight.stride_frequency_at_vmax",
    "insight.stride_length_at_vmax",
  ],
  VET: [
    "horse.age",
    "training.training_type_name",
    "training.track_name_name",
    "training.track_surface_name",
    "training.track_condition_name",
    "insight.main_work_duration_seconds",
    "insight.main_work_distance_meters",
    "insight.max_speed",
    "insight.time_best_600",
    "insight.time_best_200",
    "insight.max_heart_rate",
    "insight.z5_duration",
    "insight.oxygen_dept_time",
    "insight.bpm_after_effort",
    "insight.bpm_after_effort_percent",
    "insight.fast_recovery_quality",
    "insight.bpm_after_15_min",
    "insight.bpm_after_15_min_percent",
    "insight.heart_rate_15_min_recovery_quality",
    "insight.bpm_at_end",
    "insight.stride_length_at_60",
    "insight.stride_frequency_at_60",
    "insight.max_stride_frequency",
    "insight.max_stride_length",
    "insight.mean_hr_first_trot",
    "insight.mean_speed_first_trot",
    "insight.mean_symmetry_first_trot",
    "insight.mean_regularity_first_trot",
    "insight.mean_hr_first_canter",
    "insight.mean_speed_first_canter",
    "insight.mean_regularity_first_canter",
    "insight.main_work_avg_stride_length",
    "insight.stride_length_best_600",
    "insight.stride_length_best_200",
    "insight.stride_frequency_at_vmax",
    "insight.stride_length_at_vmax",
  ],
  SPORT: [
    "horse.age",
    "training.training_type_name",
    "training.track_name_name",
    "training.track_surface_name",
    "training.track_condition_name",
    "insight.main_work_duration_seconds",
    "insight.main_work_distance_meters",
    "insight.max_speed",
    "insight.time_best_600",
    "insight.time_best_200",
    "insight.z5_duration",
    "insight.oxygen_dept_time",
    "insight.max_heart_rate",
    "insight.bpm_after_effort",
    "insight.bpm_after_effort_percent",
    "insight.fast_recovery_quality",
    "insight.bpm_after_15_min",
    "insight.bpm_after_15_min_percent",
    "insight.heart_rate_15_min_recovery_quality",
    "insight.bpm_at_end",
    "insight.mean_hr_first_trot",
    "insight.mean_speed_first_trot",
    "insight.mean_symmetry_first_trot",
    "insight.mean_regularity_first_trot",
    "insight.mean_hr_first_canter",
    "insight.mean_speed_first_canter",
    "insight.mean_regularity_first_canter",
    "insight.main_work_avg_stride_length",
    "insight.stride_frequency_at_60",
    "insight.stride_length_at_60",
    "insight.max_stride_frequency",
    "insight.max_stride_length",
    "insight.stride_length_best_600",
    "insight.stride_length_best_200",
    "insight.stride_frequency_at_vmax",
    "insight.stride_length_at_vmax",
  ],
  GALLOP: [
    "horse.age",
    "training.training_type_name",
    "training.track_name_name",
    "training.track_surface_name",
    "training.track_condition_name",
    "insight.main_work_duration_seconds",
    "insight.main_work_distance_meters",
    "insight.max_speed",
    "insight.time_last_600",
    "insight.time_last_200",
    "insight.time_best_600",
    "insight.time_best_200",
    "insight.max_heart_rate",
    "insight.z5_duration",
    "insight.oxygen_dept_time",
    "insight.bpm_after_effort",
    "insight.bpm_after_effort_percent",
    "insight.fast_recovery_quality",
    "insight.bpm_after_15_min",
    "insight.bpm_after_15_min_percent",
    "insight.heart_rate_15_min_recovery_quality",
    "insight.bpm_at_end",
    "insight.stride_length_at_60",
    "insight.stride_frequency_at_60",
    "insight.max_stride_frequency",
    "insight.max_stride_length",
    "insight.mean_hr_first_trot",
    "insight.mean_speed_first_trot",
    "insight.mean_symmetry_first_trot",
    "insight.mean_regularity_first_trot",
    "insight.mean_hr_first_canter",
    "insight.mean_speed_first_canter",
    "insight.mean_regularity_first_canter",
  ],
};

export const useAnalyticTable = (
  enableQuery: boolean = false,
  user?: string,
  enableExportsQuery: boolean = false,
) => {
  const [groupedParams, setGroupedParams] = useState<
    Partial<Record<string, TAnalyticParams[]>>
  >({});
  const [allParams, setAllParams] = useState<TAnalyticParams[]>([]);
  const [AnalyticViews, setAnalyticViews] = useState<Partial<TAnalyticView>[]>(
    [],
  );
  const {currentLang} = useLocales()

  const default_analytics_view = useSelector<RootState>(
    (state) => state.user.user?.default_analytics_view,
  ) as string | null;

  // Queries
  const getConfigs = useQuery(
    ["table_config", user],
    () => AnalyticTable.getConfigs(user),
    {
      enabled: enableQuery && !isUndefined(user),
      staleTime: 5 * 60 * 1000,
      onSuccess: async ({ data }) => {
        let views = hydraTransform(data);

        if (default_analytics_view) {
          let id = toNumber(default_analytics_view.split("/").at(3));
          try {
            let { data } = await getViewByIdMutation.mutateAsync({ id });
            views.push(data);
          } catch (error) {
            console.warn("cannot fetch default view for user");
          }
        }

        setAnalyticViews(uniqBy(views, "id"));
      },
    },
  );

  const userExports = useQuery(
    ["user_exports", user],
    () => AnalyticTable.getExports(user),
    {
      enabled: enableExportsQuery,
      staleTime: 5 * 60 * 1000,
    },
  );

  // Mutations
  const getParametersMutation = useMutation(
    () => AnalyticTable.getAnalyticsParameters(currentLang),
    {
      onSuccess(response) {
        const rawData = get(
          response.data,
          "hydra:member",
          [],
        ) as TAnalyticParams[];

        const filteredData: TAnalyticParams[] = [];
        const groups: Partial<Record<string, TAnalyticParams[]>> = {};

        // Single O(N) pass to filter and group simultaneously
        for (let i = 0; i < rawData.length; i++) {
          const item = rawData[i];
          if (!PARAM_BLACKLIST.has(item.name)) {
            filteredData.push(item);

            const category = item.category;
            if (!groups[category]) {
              groups[category] = [];
            }
            groups[category]!.push(item);
          }
        }

        setAllParams(filteredData);
        setGroupedParams(groups);
      },
    },
  );

  const saveNewConfigMutation = useMutation((data: TNewConfig) =>
    AnalyticTable.saveNewConfig(data),
  );

  const getViewByIdMutation = useMutation(
    ({
      id,
      isInitialLoad = false,
      selectedHorse,
    }: {
      id: number;
      isInitialLoad?: boolean;
      selectedHorse?: number;
    }) => AnalyticTable.getViewById(id, isInitialLoad, selectedHorse,currentLang),
  );

  const deleteViewMutation = useMutation((id: number) =>
    AnalyticTable.deleteView(id),
  );

  const updateViewMutation = useMutation(
    ({
      id,
      data,
    }: {
      id: number;
      data: Partial<TAnalyticView> & { user?: string };
    }) => AnalyticTable.updateView(id, data),
  );

  const getFiltersMutation = useMutation(
    ({ id, selectedHorse }: { id?: number; selectedHorse?: number }) =>
      AnalyticTable.getFilters(id, selectedHorse,currentLang),
  );

  const fetchAnalyticData = useMutation(
    ({ id, data }: { id: number; data: TAnalyticData }) =>
      AnalyticTable.getAnalytics(id, data, currentLang),
  );

  const exportAnalyticDataMutation = useMutation(
    ({ data }: { data: any }) =>
      AnalyticTable.exportAnalytics(transform(data, user)),
    {
      retry: 20,
      retryDelay: 10 * 1000,
    },
  );

  // Optimized memoized getter
  const getInsightParametersNames = useCallback(
    (type: TuserAccountType = "GALLOP") => {
      return INSIGHT_PARAMETERS_MAP[type] ?? [];
    },
    [],
  );

  return {
    getParameters: getParametersMutation,
    allParams,
    groupedParams,
    saveNewConfig: saveNewConfigMutation,
    getInsightParametersNames,
    AnalyticViews,
    getViewById: getViewByIdMutation,
    viewsLoading: getConfigs.isLoading || getConfigs.isFetching,
    deleteView: deleteViewMutation,
    updateView: updateViewMutation,
    getFilters: getFiltersMutation,
    fetchAnalyticData,
    exportAnalyticDataMutation,
    setAnalyticViews,
    isExportsLoading: userExports.isLoading || userExports.isFetching,
    exports: hydraTransform(userExports.data?.data ?? []),
  };
};
